import { useCallback, useEffect, useRef, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import { PhysicalCountService } from '../../../api/physicalCountCompat';
import type {
  PhysicalCountDetailGroupKey,
  PhysicalCountDetailItem,
  PhysicalCountSession,
  PhysicalCountSessionSummary,
} from '../../../types/physicalCount.types';
import {
  PHYSICAL_COUNT_DETAIL_GROUPS,
  PHYSICAL_COUNT_GROUP_KEYS,
  PHYSICAL_COUNT_ITEMS_PAGE_SIZE,
} from './physicalCountDetailGroups';
import { PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS } from './physicalCountDetail.config';

export interface GroupUiState {
  expanded: boolean;
  page: number;
  itemsPerPage: number;
  total: number;
  totalPages: number;
  items: PhysicalCountDetailItem[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const defaultGroupState = (): GroupUiState => ({
  expanded: false,
  page: 1,
  itemsPerPage: PHYSICAL_COUNT_ITEMS_PAGE_SIZE,
  total: 0,
  totalPages: 0,
  items: [],
  loading: false,
  loaded: false,
  error: null,
});

const createInitialGroupState = (): Record<PhysicalCountDetailGroupKey, GroupUiState> =>
  Object.fromEntries(PHYSICAL_COUNT_GROUP_KEYS.map((key) => [key, defaultGroupState()])) as Record<
    PhysicalCountDetailGroupKey,
    GroupUiState
  >;

function summaryFromListRow(session: PhysicalCountSession): PhysicalCountSessionSummary {
  return {
    id: session.id,
    code: session.code,
    status: session.status,
    locationId: session.locationId,
    locationName: session.locationName,
    sessionDate: session.sessionDate,
    comparedAt: session.comparedAt,
    expectedCount: session.expectedCount,
    actualCount: session.actualCount,
    okCount: session.okCount,
    missingCount: session.missingCount,
    surplusCount: session.surplusCount,
    duplicateCount: session.duplicateCount,
    scannedNotAvailableCount: session.scannedNotAvailableCount ?? 0,
    createdBy: session.createdBy,
    createdAt: session.createdAt,
  };
}

export interface UsePhysicalCountDetailModalOptions {
  /** Si false, solo carga resumen (sin estado de grupos paginados). */
  enableInlineGroups?: boolean;
}

export function usePhysicalCountDetailModal(
  open: boolean,
  session: PhysicalCountSession | null,
  options?: UsePhysicalCountDetailModalOptions,
) {
  const enableInlineGroups =
    options?.enableInlineGroups ?? PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS;
  const [summary, setSummary] = useState<PhysicalCountSessionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [groups, setGroups] = useState(createInitialGroupState);
  const sessionIdRef = useRef<number | null>(null);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const resetState = useCallback(() => {
    setSummary(null);
    setSummaryError(null);
    setGroups(createInitialGroupState());
    sessionIdRef.current = null;
  }, []);

  useEffect(() => {
    if (!open || session?.id == null) {
      resetState();
      return;
    }

    const sessionId = session.id;
    sessionIdRef.current = sessionId;
    setSummaryLoading(true);
    setSummaryError(null);
    setGroups(createInitialGroupState());

    PhysicalCountService.sessionsFindSummary(sessionId)
      .then((mapped) => {
        if (sessionIdRef.current !== sessionId) return;
        setSummary(mapped);
        setSummaryError(null);
      })
      .catch(() => {
        if (sessionIdRef.current !== sessionId) return;
        setSummaryError('No se pudo cargar el resumen actualizado. Se muestran datos del listado.');
        setSummary(summaryFromListRow(session));
      })
      .finally(() => {
        if (sessionIdRef.current === sessionId) {
          setSummaryLoading(false);
        }
      });
  }, [open, session, resetState]);

  const fetchGroupPage = useCallback(
    async (group: PhysicalCountDetailGroupKey, page: number, sessionId: number, limit: number) => {
      setGroups((prev) => ({
        ...prev,
        [group]: { ...prev[group], loading: true, error: null },
      }));

      try {
        const mapped = await PhysicalCountService.sessionsFindItems(sessionId, {
          group,
          page,
          limit,
        });
        if (sessionIdRef.current !== sessionId) return;

        setGroups((prev) => ({
          ...prev,
          [group]: {
            ...prev[group],
            page: mapped.meta.page,
            itemsPerPage: mapped.meta.limit,
            total: mapped.meta.total,
            totalPages: mapped.meta.totalPages,
            items: mapped.data,
            loading: false,
            loaded: true,
            error: null,
          },
        }));
      } catch {
        if (sessionIdRef.current !== sessionId) return;
        setGroups((prev) => ({
          ...prev,
          [group]: {
            ...prev[group],
            loading: false,
            loaded: true,
            error: 'No se pudieron cargar los registros.',
          },
        }));
      }
    },
    [],
  );

  const toggleGroup = useCallback(
    (group: PhysicalCountDetailGroupKey) => {
      if (!enableInlineGroups) return;
      const sessionId = sessionIdRef.current;
      if (sessionId == null) return;

      const prev = groupsRef.current;
      const nextExpanded = !prev[group].expanded;
      const shouldFetch = nextExpanded && !prev[group].loaded && !prev[group].loading;
      const fetchPage = prev[group].page;

      setGroups((current) => ({
        ...current,
        [group]: { ...current[group], expanded: nextExpanded },
      }));

      if (shouldFetch) {
        void fetchGroupPage(group, fetchPage, sessionId, prev[group].itemsPerPage);
      }
    },
    [enableInlineGroups, fetchGroupPage],
  );

  const changeGroupPage = useCallback(
    (group: PhysicalCountDetailGroupKey, page: number) => {
      if (!enableInlineGroups) return;
      const sessionId = sessionIdRef.current;
      if (sessionId == null) return;

      const limit = groupsRef.current[group].itemsPerPage;
      setGroups((prev) => ({
        ...prev,
        [group]: { ...prev[group], page, expanded: true },
      }));
      void fetchGroupPage(group, page, sessionId, limit);
    },
    [enableInlineGroups, fetchGroupPage],
  );

  const changeGroupItemsPerPage = useCallback(
    (group: PhysicalCountDetailGroupKey, event: SelectChangeEvent<unknown>) => {
      if (!enableInlineGroups) return;
      const sessionId = sessionIdRef.current;
      if (sessionId == null) return;

      const newLimit = Number(event.target.value);
      setGroups((prev) => ({
        ...prev,
        [group]: { ...prev[group], page: 1, itemsPerPage: newLimit, expanded: true },
      }));
      void fetchGroupPage(group, 1, sessionId, newLimit);
    },
    [enableInlineGroups, fetchGroupPage],
  );

  const expandAllGroups = useCallback(() => {
    if (!enableInlineGroups) return;
    const sessionId = sessionIdRef.current;
    if (sessionId == null || summary == null) return;

    const prev = groupsRef.current;
    const pendingFetches: Array<{
      key: PhysicalCountDetailGroupKey;
      page: number;
      limit: number;
    }> = [];
    const next = { ...prev };

    for (const cfg of PHYSICAL_COUNT_DETAIL_GROUPS) {
      if (cfg.getCount(summary) <= 0) continue;
      if (!prev[cfg.key].loaded && !prev[cfg.key].loading) {
        pendingFetches.push({
          key: cfg.key,
          page: prev[cfg.key].page,
          limit: prev[cfg.key].itemsPerPage,
        });
      }
      next[cfg.key] = { ...prev[cfg.key], expanded: true };
    }

    setGroups(next);

    for (const { key, page, limit } of pendingFetches) {
      void fetchGroupPage(key, page, sessionId, limit);
    }
  }, [enableInlineGroups, summary, fetchGroupPage]);

  const collapseAllGroups = useCallback(() => {
    if (!enableInlineGroups) return;
    setGroups((prev) => {
      const next = { ...prev };
      for (const key of PHYSICAL_COUNT_GROUP_KEYS) {
        next[key] = { ...next[key], expanded: false };
      }
      return next;
    });
  }, [enableInlineGroups]);

  const hasExpandableGroups =
    enableInlineGroups &&
    summary != null &&
    PHYSICAL_COUNT_DETAIL_GROUPS.some((cfg) => cfg.getCount(summary) > 0);

  return {
    summary,
    summaryLoading,
    summaryError,
    enableInlineGroups,
    groups,
    toggleGroup,
    changeGroupPage,
    changeGroupItemsPerPage,
    expandAllGroups,
    collapseAllGroups,
    hasExpandableGroups,
  };
}
